/**
 * AskWidget — generic floating "Ask <noun>" chat.
 *
 * Two modes:
 *
 *  1. Standalone (default) — renders its own fixed floating button + panel.
 *     Used historically; kept for back-compat.
 *
 *  2. Controlled (pass `open` + `onClose`) — renders ONLY the panel, anchored
 *     fixed bottom-right. The button is owned by ChatWidget which stacks all
 *     page-level Ask buttons in one column so they never overlap.
 *     Pages that opt in also call usePageAsk() to publish themselves to
 *     ChatWidget.
 *
 * Props:
 *   title:      string  e.g. "Ask history" / "Ask competitors"
 *   placeholder string  shown in the input
 *   examples:   string[] suggested questions (rendered when history empty)
 *   askFn:      async (query, k) => { answer, citations[], model }
 *   onCitationClick: (citation) => void   for FE-side navigation
 *   renderCitation: (citation, idx) => ReactNode  to draw each citation card
 *   open:       boolean  (controlled mode) — panel is visible when true
 *   onClose:    function (controlled mode) — called when user closes panel
 */
import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Sparkles, Send, X, Loader2 } from "lucide-react";
import { clearPageAsk, registerPageAsk } from "@/lib/pageAskRegistry";

export default function AskWidget({
  title = "Ask",
  placeholder = "Ask a question…",
  examples = [],
  askFn,
  onCitationClick,
  renderCitation,
  // Controlled-mode props
  open: openProp,
  onClose,
  onOpen,
  // Color hint for the ChatWidget button (tailwind bg class)
  buttonColor = "bg-violet-600 hover:bg-violet-700",
}) {
  const controlled = openProp !== undefined;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlled ? openProp : ownOpen;
  const setOpen = controlled
    ? (v) => { if (v) onOpen?.(); else onClose?.(); }
    : setOwnOpen;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const listRef = useRef(null);

  // Register into ChatWidget column when controlled mode is active
  useEffect(() => {
    if (!controlled) return;
    registerPageAsk({ title, buttonColor, onOpen: () => onOpen?.() });
    return () => clearPageAsk();
  }, [controlled, title, buttonColor, onOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const ask = async () => {
    const q = query.trim();
    if (!q || loading) return;
    setLoading(true);
    try {
      const data = await askFn(q, 8);
      setHistory((h) => [
        ...h,
        {
          q,
          answer: data.answer || "(no answer)",
          citations: data.citations || [],
          model: data.model,
        },
      ]);
      setQuery("");
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    } catch (err) {
      setHistory((h) => [
        ...h,
        {
          q,
          answer:
            err?.response?.data?.detail ||
            err?.message ||
            "Request failed.",
          citations: [],
          model: "error",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  // Standalone mode — still renders its own button when not controlled
  if (!controlled && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors"
        title={title}
      >
        <Sparkles className="w-4 h-4" /> {title}
      </button>
    );
  }

  if (!open) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] w-[420px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col"
      style={{ maxHeight: "70vh" }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 rounded-t-xl">
        <div className="inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-slate-800 text-sm">{title}</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {history.length === 0 && examples.length > 0 && (
          <div className="text-xs text-slate-500 leading-relaxed">
            <p className="mb-2">Try questions like:</p>
            <ul className="list-disc pl-4 space-y-1">
              {examples.map((ex, i) => <li key={i}>"{ex}"</li>)}
            </ul>
          </div>
        )}
        {history.map((h, i) => (
          <div key={i} className="space-y-2">
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-900">
              <MessageCircle className="w-3 h-3 inline mr-1" /> {h.q}
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">
              {h.answer}
            </div>
            {h.citations.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  Citations
                </p>
                {h.citations.map((c, idx) =>
                  renderCitation
                    ? renderCitation(c, idx, () => onCitationClick?.(c))
                    : (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => onCitationClick?.(c)}
                        className="w-full text-left text-xs px-2 py-1.5 rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50"
                      >
                        [#{idx + 1}] {c.label || c.name || c.id}
                      </button>
                    )
                )}
              </div>
            )}
            {h.model && h.model !== "error" && (
              <p className="text-[10px] text-slate-400">model: {h.model}</p>
            )}
          </div>
        ))}
        {loading && (
          <div className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Searching…
          </div>
        )}
      </div>

      <div className="border-t p-3 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={loading}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-blue-500"
        />
        <button
          type="button"
          onClick={ask}
          disabled={loading || !query.trim()}
          className="px-3 py-2 rounded-md bg-blue-600 text-white disabled:bg-slate-300 hover:bg-blue-700 inline-flex items-center"
          title="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
